import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import apiClient from "../../services/api/config";

import { useReviewerLogicQuizOnly } from "../../hooks/useReviewerLogicQuizOnly";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import SmartText from "../../components/SmartText";
import ActiveExam from "./ActiveExam";
import QuizEditor from "../../components/CreateReviewer/QuizEditor";
import { generatePDFDocument } from "../../utils/pdfGenerator";

const MAX_EXAM_NAME_LENGTH = 30;

const styles = {
  blue: {
    dot: "bg-blue-50",
    iconBg: "bg-blue-100",
    pickerBg: "bg-blue-500",
    iconText: "text-blue-600",
    hoverBg: "group-hover:bg-blue-600",
    hoverText: "text-blue-500",
    ring: "ring-blue-500",
    border: "hover:border-blue-200",
    gradient: "from-blue-500",
    button: "bg-blue-600 hover:bg-blue-700 shadow-blue-600/30",
    badge: "bg-blue-50 text-blue-700 border-blue-100",
  },
  orange: {
    dot: "bg-orange-50",
    iconBg: "bg-orange-100",
    pickerBg: "bg-orange-500",
    iconText: "text-orange-600",
    hoverBg: "group-hover:bg-orange-500",
    hoverText: "text-orange-500",
    ring: "ring-orange-500",
    border: "hover:border-orange-200",
    gradient: "from-orange-500",
    button: "bg-orange-600 hover:bg-orange-700 shadow-orange-600/30",
    badge: "bg-orange-50 text-orange-700 border-orange-100",
  },
  purple: {
    dot: "bg-purple-50",
    iconBg: "bg-purple-100",
    pickerBg: "bg-purple-500",
    iconText: "text-purple-600",
    hoverBg: "group-hover:bg-purple-600",
    hoverText: "text-purple-500",
    ring: "ring-purple-500",
    border: "hover:border-purple-200",
    gradient: "from-purple-500",
    button: "bg-purple-600 hover:bg-purple-700 shadow-purple-600/30",
    badge: "bg-purple-50 text-purple-700 border-purple-100",
  },
  emerald: {
    dot: "bg-emerald-50",
    iconBg: "bg-emerald-100",
    pickerBg: "bg-emerald-500",
    iconText: "text-emerald-600",
    hoverBg: "group-hover:bg-emerald-600",
    hoverText: "text-emerald-500",
    ring: "ring-emerald-500",
    border: "hover:border-emerald-200",
    gradient: "from-emerald-500",
    button: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/30",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
  },
  red: {
    dot: "bg-red-50",
    iconBg: "bg-red-100",
    pickerBg: "bg-red-500",
    iconText: "text-red-600",
    hoverBg: "group-hover:bg-red-600",
    hoverText: "text-red-500",
    ring: "ring-red-500",
    border: "hover:border-red-200",
    gradient: "from-red-500",
    button: "bg-red-600 hover:bg-red-700 shadow-red-600/30",
    badge: "bg-red-50 text-red-700 border-red-100",
  },
  gray: {
    dot: "bg-gray-50",
    iconBg: "bg-gray-100",
    pickerBg: "bg-gray-500",
    iconText: "text-gray-600",
    hoverBg: "group-hover:bg-gray-600",
    hoverText: "text-gray-500",
    ring: "ring-gray-500",
    border: "hover:border-gray-200",
    gradient: "from-gray-500",
    button: "bg-gray-600 hover:bg-gray-700 shadow-gray-600/30",
    badge: "bg-gray-50 text-gray-700 border-gray-100",
  },
};

const getAxiosErrorMessage = (error, fallback = "Something went wrong.") => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

const inferQuizType = (q) => {
  const options = Array.isArray(q?.options) ? q.options : [];
  const question = String(q?.question || "");

  if (options.length >= 2) {
    const normalized = options.map((o) => String(o || "").trim().toLowerCase());
    if (normalized.includes("true") && normalized.includes("false")) return "true-false";
    return "mcq";
  }
  if (question.includes("_______") || question.includes("____")) return "fill-blanks";
  return "identification";
};

const StudentMadeExam = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => setIsSidebarOpen((p) => !p);

  // URL Params
  const titleParam = searchParams.get("title") || location.state?.title || "My Exams";
  const examIdParam = searchParams.get("examId") || location.state?.examId || null;

  // AI Logic Hook
  const { state: logicState, actions: logicActions } = useReviewerLogicQuizOnly();
  const {
    isProcessing,
    loadingText,
    aiConfig,
    generatedData,
    title: hookTitle,
    textInput,
    uploadedFiles,
    rawContent,
  } = logicState;

  const {
    handleGenerate,
    setTitle: setHookTitle,
    setTextInput,
    setUploadedFiles,
    updateConfig,
    updateGeneratedQuiz,
    resetForm,
    setFolderId,
  } = logicActions;

  const handleDownloadReviewPdf = () => {
    if (!generatedData?.quiz?.length) {
      Swal.fire("Error", "No quiz questions to export.", "warning");
      return;
    }

    const quizTypeArr = Array.isArray(aiConfig?.quizType)
      ? aiConfig.quizType
      : [aiConfig?.quizType || "mcq"];

    generatePDFDocument({
      title: hookTitle || "AI Exam",
      generatedData: {
        summary: "",
        flashcards: [],
        quiz: generatedData.quiz || [],
      },
      config: {
        ...aiConfig,
        summaryEnabled: false,
        flashcardsEnabled: false,
        quizEnabled: true,
        quizType: quizTypeArr,
      },
    });
  };

  // Local State
  const [view, setView] = useState("library");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); 
  
  // ✅ NEW: Custom Rename Modal States
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [examToEdit, setExamToEdit] = useState(null);
  const [renameInput, setRenameInput] = useState("");

  const [activeSubject, setActiveSubject] = useState(null);
  const [inputMode, setInputMode] = useState("text");
  const [selectedColor, setSelectedColor] = useState("blue");
  const [sessionTime, setSessionTime] = useState(60);
  const [progress, setProgress] = useState(0);
  const [isSavingToDashboard, setIsSavingToDashboard] = useState(false);
  const [hasSavedToDashboard, setHasSavedToDashboard] = useState(false);

  // Data State
  const [library, setLibrary] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [isLibraryLoading, setIsLibraryLoading] = useState(true);

  // Question Types
  const questionTypes = [
    { id: "mcq", label: "Multiple Choice", icon: "fa-list-ul", color: "text-blue-500", bg: "bg-blue-50" },
    { id: "identification", label: "Identification", icon: "fa-magnifying-glass", color: "text-purple-500", bg: "bg-purple-50" },
    { id: "true-false", label: "True / False", icon: "fa-check-double", color: "text-emerald-500", bg: "bg-emerald-50" },
    { id: "fill-blanks", label: "Fill in Blanks", icon: "fa-pen-to-square", color: "text-amber-500", bg: "bg-amber-50" },
  ];

  const handleAuthExpired = useCallback(() => navigate("/login"), [navigate]);

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setIsLibraryLoading(true);
    try {
      const libRes = await apiClient.get(`/exam-templates/my`);
      let templates = libRes.data?.examTemplates || libRes.data?.data || [];
      if (!Array.isArray(templates)) templates = [];
      setLibrary(templates);

      const resultsRes = await apiClient.get(`/results/all`);
      setExamResults(resultsRes.data?.data || []);
    } catch (error) {
      if (error?.response?.status === 401) return handleAuthExpired();
      console.error("Dashboard Load Error:", error);
    } finally {
      setIsLibraryLoading(false);
    }
  }, [handleAuthExpired]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const filteredLibrary = useMemo(() => {
    if (!examIdParam) return library; 
    return library.filter((t) => {
      const parentExamId = typeof t.examId === "object" && t.examId !== null ? t.examId._id : t.examId;
      return String(parentExamId) === String(examIdParam);
    });
  }, [library, examIdParam]);

  useEffect(() => {
    if (!examIdParam || isLibraryLoading || library.length === 0) return;
    const directTemplateMatch = library.find((t) => String(t._id) === String(examIdParam));
    if (directTemplateMatch) {
      setActiveSubject({
        id: directTemplateMatch._id,
        name: directTemplateMatch.topic,
        questions: [], 
        color: directTemplateMatch.color || "blue",
        __template: directTemplateMatch,
      });
      setIsStartModalOpen(true);
    }
  }, [examIdParam, library, isLibraryLoading]);

  const openCreateModal = () => {
    if (examIdParam && setFolderId) setFolderId(examIdParam);
    setHasSavedToDashboard(false);
    setIsSavingToDashboard(false);
    setIsCreateModalOpen(true);
  };

  const saveToBackend = async () => {
    if (isSavingToDashboard || hasSavedToDashboard) return;
    const cleanTitle = String(hookTitle || "").trim();
    if (cleanTitle && cleanTitle.length > MAX_EXAM_NAME_LENGTH) {
      return Swal.fire(
        "Failed",
        `Exam name must not exceed ${MAX_EXAM_NAME_LENGTH} characters.`,
        "warning",
      );
    }
    setIsSavingToDashboard(true);

    try {
      Swal.fire({
        title: "Minting to Blockchain...",
        text: "Encrypting & Uploading to IPFS",
        didOpen: () => Swal.showLoading(),
        allowOutsideClick: false,
        background: "#fff",
        color: "#1e293b",
      });

      const quizTypeArr = Array.isArray(aiConfig?.quizType) ? aiConfig.quizType : [aiConfig?.quizType || "mcq"];
      const payload = {
        examId: examIdParam || null,
        topic: cleanTitle || "Untitled Exam",
        generatedData: { quiz: generatedData?.quiz || [], flashcards: [], summary: "" },
        config: { ...aiConfig, quizType: quizTypeArr, examType: quizTypeArr },
        color: selectedColor,
      };

      const res = await apiClient.post("/exam-templates/save", payload);
      const created = res.data?.examTemplate || res.data?.data || null;

      if (created) setLibrary((prev) => [created, ...prev]);
      setIsCreateModalOpen(false);
      resetForm();
      setSelectedColor("blue");
      setHasSavedToDashboard(true);
      Swal.fire("Success", "Exam minted and saved to dashboard!", "success");
    } catch (error) {
      if (error?.response?.status === 401) return handleAuthExpired();
      Swal.fire("Error", getAxiosErrorMessage(error, "Failed to save exam to blockchain."), "error");
    } finally {
      setIsSavingToDashboard(false);
    }
  };

  const handleStartClick = async (templateItem) => {
    try {
      Swal.fire({ title: "Decrypting...", text: "Fetching from IPFS", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
      const res = await apiClient.get(`/exam-templates/${templateItem._id}`);
      const decryptedData = res.data?.data ?? res.data;
      const content = typeof decryptedData === "string" ? JSON.parse(decryptedData) : decryptedData;

      if (!content?.quiz || content.quiz.length === 0) return Swal.fire("Error", "No quiz questions found.", "warning");

      setActiveSubject({
        id: templateItem._id,
        name: templateItem.topic,
        questions: content.quiz,
        color: templateItem.color || "blue",
      });
      setSessionTime(60);
      Swal.close();
      setIsStartModalOpen(true);
    } catch (error) {
      if (error?.response?.status === 401) return handleAuthExpired();
      Swal.fire("Error", getAxiosErrorMessage(error, "Failed to load exam."), "error");
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setActiveDropdown(null);
    Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EF4444",
        cancelButtonColor: "#94A3B8",
        confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
        if (result.isConfirmed) {
            try {
                await apiClient.delete(`/exam-templates/${id}`);
                setLibrary((prev) => prev.filter((exam) => exam._id !== id));
                Swal.fire("Deleted!", "Your exam has been deleted.", "success");
            } catch (error) {
                Swal.fire("Error", getAxiosErrorMessage(error, "Failed to delete exam."), "error");
            }
        }
    });
  };

  // ✅ NEW: Open the React Modal
  const openRenameModal = (e, exam) => {
    e.stopPropagation(); 
    setActiveDropdown(null);
    setExamToEdit(exam);
    setRenameInput(exam.topic);
    setIsRenameModalOpen(true);
  };

  // ✅ NEW: Submit the rename API Call
  const submitRename = async () => {
    const cleanName = renameInput.trim();
    if (!examToEdit || !cleanName) return;
    if (cleanName.length > MAX_EXAM_NAME_LENGTH) {
      return Swal.fire(
        "Failed",
        `Exam name must not exceed ${MAX_EXAM_NAME_LENGTH} characters.`,
        "warning",
      );
    }

    try {
      await apiClient.put(`/exam-templates/${examToEdit._id}`, { topic: cleanName });
      setLibrary((prev) => 
          prev.map((item) => item._id === examToEdit._id ? { ...item, topic: cleanName } : item)
      );
      setIsRenameModalOpen(false);
      Swal.fire("Success", "Exam renamed successfully.", "success");
    } catch (error) {
      Swal.fire("Error", getAxiosErrorMessage(error, "Failed to rename exam."), "error");
    }
  };

  const launchExam = async () => {
    try {
      setIsStartModalOpen(false);
      if (activeSubject && (!activeSubject.questions || activeSubject.questions.length === 0)) {
        const templateId = activeSubject.__template?._id || activeSubject.id;
        if (templateId) {
          Swal.fire({ title: "Decrypting...", text: "Fetching from IPFS", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
          const res = await apiClient.get(`/exam-templates/${templateId}`);
          const decryptedData = res.data?.data ?? res.data;
          const content = typeof decryptedData === "string" ? JSON.parse(decryptedData) : decryptedData;

          if (!content?.quiz || content.quiz.length === 0) {
            Swal.fire("Error", "No quiz questions found.", "warning");
            setIsStartModalOpen(true);
            return;
          }

          setActiveSubject((prev) => ({
            id: templateId,
            name: prev?.name || "Exam",
            questions: content.quiz,
            color: prev?.color || "blue",
          }));
          Swal.close();
        }
      }
      setProgress(0);
      setView("loading");
    } catch (error) {
      Swal.fire("Error", getAxiosErrorMessage(error, "Failed to start exam."), "error");
      setIsStartModalOpen(true);
    }
  };

  const handleExamCompletion = async (resultDetails) => {
    const { correct, total, percentage, attempts, timeSpentSeconds } = resultDetails;
    try {
      Swal.fire({ title: "Saving Results...", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
      const payload = { reviewerId: activeSubject.id, mode: "exam", score: correct, totalQuestions: total, timeSpentSeconds, attempts };
      await apiClient.post("/results/submit", payload);

      Swal.fire({
        title: "Exam Completed",
        text: `You scored ${percentage}% (${correct} out of ${total})`,
        icon: percentage > 70 ? "success" : "info",
      });
      fetchDashboardData();
    } catch (error) {
      if (error?.response?.status === 401) return handleAuthExpired();
      Swal.fire("Notice", `You scored ${percentage}%, but we couldn't save your results online.`, "warning");
    } finally {
      setActiveSubject(null);
      setView("library");
    }
  };

  const handleExitExam = () => {
    Swal.fire({
      title: "Exit Exam?",
      text: "Progress will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Exit",
    }).then((res) => {
      if (res.isConfirmed) {
        setActiveSubject(null);
        setView("library");
      }
    });
  };

  useEffect(() => {
    if (view === "loading") {
      let p = 0;
      const interval = setInterval(() => {
        p += 5;
        setProgress(p);
        if (p >= 100) {
          clearInterval(interval);
          setView("active_exam");
        }
      }, 80);
      return () => clearInterval(interval);
    }
  }, [view]);

  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedFiles(Array.from(e.target.files));
    }
    try {
      e.target.value = '';
    } catch (_) {}
  };

  const toggleQuizType = (typeId) => {
    const currentTypes = Array.isArray(aiConfig?.quizType) ? aiConfig.quizType : [aiConfig?.quizType || "mcq"];
    const newTypes = currentTypes.includes(typeId) ? currentTypes.filter((t) => t !== typeId) : [...currentTypes, typeId];
    if (newTypes.length === 0 && currentTypes.includes(typeId)) return;
    updateConfig("quizType", newTypes);
  };

  const onGenerateClick = () => {
    const cleanTitle = String(hookTitle || "").trim();
    if (!cleanTitle) return Swal.fire("Error", "Please enter a topic.", "warning");
    if (cleanTitle.length > MAX_EXAM_NAME_LENGTH) {
      return Swal.fire(
        "Failed",
        `Exam name must not exceed ${MAX_EXAM_NAME_LENGTH} characters.`,
        "warning",
      );
    }
    if (inputMode === "text" && !textInput) return Swal.fire("Error", "Please enter some text.", "warning");
    if (inputMode === "file" && (!uploadedFiles || uploadedFiles.length === 0)) return Swal.fire("Error", "Please upload a file.", "warning");
    setHasSavedToDashboard(false);
    setIsSavingToDashboard(false);
    handleGenerate(inputMode);
  };

  const handleColorChange = async (e, exam, newColor) => {
    e.stopPropagation();
    setActiveDropdown(null);

    const prevLibrary = library;
    setLibrary((prev) =>
      prev.map((item) => (item._id === exam._id ? { ...item, color: newColor } : item))
    );

    try {
      await apiClient.put(`/exam-templates/${exam._id}`, { color: newColor });
      if (activeSubject && String(activeSubject.id) === String(exam._id)) {
        setActiveSubject((prev) => (prev ? { ...prev, color: newColor } : prev));
      }
    } catch (error) {
      setLibrary(prevLibrary);
      Swal.fire("Error", getAxiosErrorMessage(error, "Failed to update exam color."), "error");
    }
  };

  const handleDiscard = () => {
    Swal.fire({
      title: "Discard results?",
      text: "You will lose the generated quiz.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, discard",
      confirmButtonColor: "#EF4444",
    }).then((result) => {
      if (result.isConfirmed) {
        resetForm();
        setSelectedColor("blue");
        setHasSavedToDashboard(false);
        setIsSavingToDashboard(false);
      }
    });
  };

  const dashboard = useMemo(() => {
    const total = filteredLibrary.length; 
    const relevantResults = (examResults || []).filter((r) => {
      const resultReviewerId = r?.reviewerId && typeof r.reviewerId === "object" ? r.reviewerId._id : r.reviewerId;
      return filteredLibrary.some(t => String(t._id) === String(resultReviewerId));
    });

    let lastScore = null;
    let readiness = 0;
    let uniqueExamsTaken = 0;

    if (relevantResults.length > 0) {
      const sortedResults = [...relevantResults].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      lastScore = sortedResults[0]?.percentage ?? null;

      const latestScoresByExam = new Map();
      sortedResults.forEach((result) => {
        const rid = result?.reviewerId && typeof result.reviewerId === "object" ? String(result.reviewerId._id) : String(result.reviewerId);
        if (!latestScoresByExam.has(rid)) latestScoresByExam.set(rid, Number(result?.percentage ?? 0));
      });

      uniqueExamsTaken = latestScoresByExam.size;
      let sum = 0;
      latestScoresByExam.forEach((score) => (sum += score));
      if (uniqueExamsTaken > 0) readiness = Math.round(sum / uniqueExamsTaken);
    }

    return { total, examsTaken: uniqueExamsTaken, lastScore, readiness };
  }, [filteredLibrary, examResults]);

  return (
    <div className="bg-slate-50 text-slate-900 font-sans flex h-screen overflow-hidden selection:bg-indigo-100 selection:text-indigo-700">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} title={titleParam} />

        <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:px-12 relative bg-slate-50/50">
          {view === "library" && (
            <div className="max-w-7xl mx-auto w-full animate-in fade-in duration-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
                    {examIdParam ? titleParam : "All Assessments"}
                  </h1>
                  <p className="text-slate-500 mt-1">
                    {examIdParam ? "Exams in this folder." : "Manage all your AI exams."}
                  </p>
                </div>
                <button
                  onClick={openCreateModal}
                  className="px-6 py-3 rounded-xl font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/30 transition-all hover:-translate-y-1 flex items-center gap-2 self-start md:self-auto"
                >
                  <i className="fa-solid fa-plus" /> New Exam
                </button>
              </div>

              {/* DASHBOARD CARDS */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col justify-between relative overflow-hidden">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Library Size</p>
                      <p className="text-4xl font-black text-slate-800 mt-2">{dashboard.total}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl">
                      <i className="fa-solid fa-layer-group"></i>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 font-medium">Generated AI Assessments</p>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col justify-between relative overflow-hidden">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Last Attempt</p>
                      <div className="flex items-end gap-2 mt-2">
                        <p className="text-4xl font-black text-slate-800">
                          {dashboard.lastScore !== null ? `${dashboard.lastScore}%` : "--"}
                        </p>
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${
                        dashboard.lastScore >= 75 ? "bg-emerald-50 text-emerald-600" :
                        dashboard.lastScore >= 50 ? "bg-amber-50 text-amber-600" : "bg-slate-50 text-slate-400"
                      }`}>
                      <i className="fa-solid fa-bolt"></i>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 font-medium">
                    {dashboard.lastScore !== null ? "Your most recent score" : "No exams taken yet"}
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col justify-between relative overflow-hidden">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Overall Readiness</p>
                      <p className="text-4xl font-black text-indigo-600 mt-2">
                        {dashboard.examsTaken > 0 ? `${dashboard.readiness}%` : "--"}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl">
                      <i className="fa-solid fa-chart-pie"></i>
                    </div>
                  </div>
                  {dashboard.examsTaken > 0 ? (
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                      <div className={`h-1.5 rounded-full transition-all duration-1000 ${
                          dashboard.readiness >= 75 ? "bg-emerald-500" :
                          dashboard.readiness >= 50 ? "bg-amber-500" : "bg-red-500"
                        }`}
                        style={{ width: `${dashboard.readiness}%` }}
                      ></div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 font-medium">Take an exam to calculate</p>
                  )}
                </div>
              </div>

              {/* EMPTY STATE */}
              {!isLibraryLoading && filteredLibrary.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50">
                  <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center text-3xl mb-4">
                    <i className="fa-solid fa-folder-open"></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-700">No exams here yet</h3>
                  <p className="text-slate-500 mb-6">Create your first AI exam for this subject.</p>
                  <button onClick={openCreateModal} className="px-6 py-2 bg-white border border-slate-300 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                    Create Exam
                  </button>
                </div>
              )}

              {/* LIBRARY GRID */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
                {isLibraryLoading
                  ? Array(3).fill(0).map((_, i) => (
                      <div key={i} className="h-64 bg-white rounded-3xl animate-pulse border border-slate-100"></div>
                    ))
                  : filteredLibrary.map((sub) => {
                      const theme = styles[sub.color] || styles.blue;
                      const itemCount = sub.config?.questionCount || 0;

                      const cardAttempts = (examResults || []).filter((r) => {
                        const rid = r?.reviewerId && typeof r.reviewerId === "object" ? r.reviewerId._id : r.reviewerId;
                        return String(rid) === String(sub._id);
                      });

                      let lastScoreText = "No attempts";
                      let lastScoreColor = "text-slate-400 font-medium italic";

                      if (cardAttempts.length > 0) {
                        const sortedAttempts = [...cardAttempts].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
                        const latestScore = Number(sortedAttempts[0]?.percentage ?? 0);
                        lastScoreText = `${latestScore}%`;
                        if (latestScore >= 75) lastScoreColor = "text-emerald-600 font-black";
                        else if (latestScore >= 50) lastScoreColor = "text-amber-500 font-black";
                        else lastScoreColor = "text-red-500 font-black";
                      }

                      return (
                        <div
                          key={sub._id}
                          onClick={() => handleStartClick(sub)}
                          className={`group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between min-h-[260px] h-auto relative overflow-hidden cursor-pointer ${theme.border}`}
                        >
                          <div className="flex-grow flex flex-col">
                            <div className="flex justify-between items-start mb-5 relative z-10">
                              <div className={`w-12 h-12 rounded-xl ${theme.iconBg} ${theme.iconText} flex items-center justify-center text-xl shadow-sm`}>
                                <i className="fa-solid fa-book"></i>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {sub.blockchain?.isMinted && (
                                  <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-600 px-2 py-1 rounded-md border border-emerald-100">
                                    <i className="fa-solid fa-link"></i> On-Chain
                                  </div>
                                )}
                                
                                {/* Kebab/Burger Options Menu */}
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(activeDropdown === sub._id ? null : sub._id);
                                    }}
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                                  >
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                  </button>
                                  
                                  {activeDropdown === sub._id && (
                                    <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                                      <button
                                        onClick={(e) => openRenameModal(e, sub)}
                                        className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                                      >
                                        <i className="fa-solid fa-pen mr-2"></i> Rename
                                      </button>
                                      <div className="px-4 py-2">
                                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Change color</p>
                                        <div className="flex items-center gap-2">
                                          {Object.keys(styles).map((colorKey) => (
                                            <button
                                              key={colorKey}
                                              type="button"
                                              onClick={(e) => handleColorChange(e, sub, colorKey)}
                                              className={`w-5 h-5 rounded-full ${styles[colorKey].pickerBg} ring-1 ring-offset-1 ${
                                                (sub.color || "blue") === colorKey ? "ring-slate-400 scale-110" : "ring-transparent"
                                              } hover:scale-110 transition-transform`}
                                              title={colorKey}
                                            />
                                          ))}
                                        </div>
                                      </div>
                                      <button
                                        onClick={(e) => handleDelete(e, sub._id)}
                                        className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                                      >
                                        <i className="fa-solid fa-trash mr-2"></i> Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <h3 className={`font-black text-xl text-slate-800 line-clamp-2 leading-tight mb-4 ${theme.hoverText} transition-colors`}>
                              {sub.topic}
                            </h3>

                            <div className="mt-auto space-y-3">
                              <div className="flex items-center justify-between">
                                <span className={`text-[11px] font-bold px-2 py-1 rounded-md border ${theme.badge} uppercase tracking-wide`}>
                                  {itemCount} Items
                                </span>
                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">
                                  {new Date(sub.createdAt).toLocaleDateString()}
                                </p>
                              </div>

                              <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Last Attempt</span>
                                <span className={`text-sm ${lastScoreColor}`}>{lastScoreText}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-5 pt-5 border-t border-slate-50">
                            <button
                              className={`w-full py-3 rounded-xl font-bold text-sm bg-slate-50 text-slate-600 ${theme.hoverBg} group-hover:text-white transition-all flex justify-between px-5 items-center`}
                            >
                              <span>Start Exam</span>
                              <i className="fa-solid fa-arrow-right opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
              </div>
            </div>
          )}

          {/* LOADING VIEW */}
          {view === "loading" && (
            <div className="flex flex-col items-center justify-center h-[60vh]">
              <div className="relative w-24 h-24 mb-8">
                <div className="absolute inset-0 rounded-full border-4 border-slate-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center text-indigo-600">
                  <i className="fa-solid fa-brain text-2xl animate-pulse"></i>
                </div>
              </div>
              <h3 className="text-2xl font-black text-slate-800 mb-2">Setting up your Exam</h3>
              <p className="text-slate-400 mb-8">Decrypting data and preparing environment...</p>
              <div className="w-64 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-600 transition-all duration-100 ease-out" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          )}

          {/* ACTIVE EXAM */}
          {view === "active_exam" && activeSubject && (
            <ActiveExam
              questions={activeSubject.questions}
              timeLimit={sessionTime}
              title={activeSubject.name}
              onComplete={handleExamCompletion}
              onExit={handleExitExam}
            />
          )}

          {/* RENAME EXAM MODAL */}
          {isRenameModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
              <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Rename Exam</h3>
                
                <input 
                  type="text" 
                  autoFocus
                  value={renameInput}
                  onChange={(e) => setRenameInput(e.target.value)}
                  maxLength={30}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition font-semibold text-slate-800 mb-6"
                  placeholder="Exam Name"
                  onKeyDown={(e) => e.key === 'Enter' && submitRename()}
                />

                <div className="flex gap-3">
                  <button 
                    onClick={() => setIsRenameModalOpen(false)}
                    className="flex-1 py-2.5 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={submitRename}
                    className="flex-1 py-2.5 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition shadow-md hover:shadow-lg"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* CREATE EXAM MODAL */}
          {isCreateModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsCreateModalOpen(false)}></div>
              <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] relative z-10 animate-in fade-in zoom-in-95 duration-300">
                <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-20">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">
                      {generatedData ? "Review Exam" : "Create New Exam"}
                    </h2>
                    <p className="text-slate-500 text-sm">
                      {generatedData ? "Verify the questions below before minting." : "Configure your AI-generated assessment."}
                    </p>
                  </div>
                  <button onClick={() => setIsCreateModalOpen(false)} className="w-10 h-10 rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-red-500 transition-colors flex items-center justify-center">
                    <i className="fa-solid fa-xmark text-lg"></i>
                  </button>
                </div>

                <div className="p-8 overflow-y-auto space-y-8 custom-scrollbar">
                  {!generatedData ? (
                    <>
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Exam Topic</label>
                        <input
                          type="text"
                          value={hookTitle}
                          onChange={(e) => setHookTitle(e.target.value)}
                          maxLength={30}
                          placeholder="e.g. Microbiology, Philippine History, ReactJS"
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition font-bold text-slate-800"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Theme Color</label>
                        <div className="flex gap-3">
                          {Object.keys(styles).map((color) => (
                            <button
                              key={color}
                              onClick={() => setSelectedColor(color)}
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                selectedColor === color ? `ring-2 ring-offset-2 ${styles[color].ring}` : "opacity-50 hover:opacity-100"
                              } ${styles[color].pickerBg}`}
                            >
                              {selectedColor === color && <i className="fa-solid fa-check text-white"></i>}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2">Difficulty</label>
                          <div className="flex bg-slate-100 p-1 rounded-xl">
                            {[1, 2, 3].map((lvl) => (
                              <button
                                key={lvl}
                                onClick={() => updateConfig("difficulty", lvl)}
                                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                                  aiConfig.difficulty === lvl ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                                }`}
                              >
                                {lvl === 1 ? "Easy" : lvl === 2 ? "Medium" : "Hard"}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2">Question Count</label>
                          <div className="flex bg-slate-100 p-1 rounded-xl">
                            {[10, 20, 30, 50, 100].map((num) => (
                              <button
                                key={num}
                                onClick={() => updateConfig("questionCount", num)}
                                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                                  aiConfig.questionCount === num ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                                }`}
                              >
                                {num}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Question Types</label>
                        <div className="grid grid-cols-2 gap-3">
                          {questionTypes.map((type) => {
                            const isSelected = (Array.isArray(aiConfig.quizType) ? aiConfig.quizType : [aiConfig.quizType]).includes(type.id);
                            return (
                              <button
                                key={type.id}
                                onClick={() => toggleQuizType(type.id)}
                                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                  isSelected ? `border-indigo-500 bg-indigo-50 text-indigo-700` : "border-slate-200 hover:border-slate-300 text-slate-500"
                                }`}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${type.bg} ${type.color}`}>
                                  <i className={`fa-solid ${type.icon}`}></i>
                                </div>
                                <span className="font-bold text-sm">{type.label}</span>
                                {isSelected && <i className="fa-solid fa-check ml-auto text-indigo-600"></i>}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <div className="flex gap-4 border-b border-slate-100 mb-4">
                          <button
                            type="button"
                            onClick={() => setInputMode("text")}
                            className={`pb-2 text-sm font-bold border-b-2 transition ${
                              inputMode === "text"
                                ? "border-indigo-600 text-indigo-600"
                                : "border-transparent text-slate-400"
                            }`}
                          >
                            Paste Text
                          </button>
                          <button
                            type="button"
                            onClick={() => setInputMode("file")}
                            className={`pb-2 text-sm font-bold border-b-2 transition ${
                              inputMode === "file"
                                ? "border-indigo-600 text-indigo-600"
                                : "border-transparent text-slate-400"
                            }`}
                          >
                            Upload File
                          </button>
                        </div>

                        {inputMode === "text" ? (
                          <textarea
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            className="w-full h-40 p-4 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition bg-slate-50 text-sm font-medium"
                            placeholder="Paste your lecture notes, article, or summary here..."
                          ></textarea>
                        ) : (
                          <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50 transition cursor-pointer relative">
                            <input
                              type="file"
                              multiple
                              accept=".pdf,.PDF,application/pdf,.docx,.DOCX,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.txt,.TXT,text/plain,.md,.MD,text/markdown,.json,application/json"
                              onChange={onFileChange}
                              className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <i className="fa-solid fa-cloud-arrow-up text-3xl text-slate-300 mb-2"></i>
                            <p className="text-sm font-bold text-slate-600">Click to Upload</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {uploadedFiles.length > 0
                                ? `${uploadedFiles.length} files selected`
                                : "Supports PDF, DOCX, TXT"}
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <QuizEditor
                      questions={generatedData.quiz || []}
                      config={aiConfig}
                      onUpdateQuiz={updateGeneratedQuiz}
                      originalContent={rawContent || textInput}
                    />
                  )}
                </div>

                <div className="p-6 border-t border-slate-100 bg-white flex justify-end gap-3 sticky bottom-0 z-20">
                  {!generatedData ? (
                    <button
                      type="button"
                      onClick={onGenerateClick}
                      disabled={isProcessing}
                      className={`px-8 py-4 rounded-xl font-bold text-white transition-all hover:-translate-y-1 active:translate-y-0 ${
                        isProcessing
                          ? "bg-slate-400 cursor-not-allowed shadow-none"
                          : `${styles[selectedColor]?.button || styles.blue.button} shadow-lg`
                      }`}
                    >
                      {isProcessing ? (
                        <span className="flex items-center gap-2">
                          <i className="fa-solid fa-circle-notch fa-spin"></i> {loadingText}
                        </span>
                      ) : (
                        "Generate AI Draft"
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleDiscard}
                        className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-50 hover:text-red-500 transition-colors"
                      >
                        Discard
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadReviewPdf}
                        className="px-6 py-3 rounded-xl font-bold text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors flex items-center gap-2"
                      >
                        <i className="fa-solid fa-file-pdf text-red-500"></i>
                        Download PDF
                      </button>
                      <button
                        type="button"
                        onClick={saveToBackend}
                        disabled={isSavingToDashboard || hasSavedToDashboard}
                        className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg transition-all flex items-center gap-2 ${
                          isSavingToDashboard || hasSavedToDashboard
                            ? "bg-emerald-300 cursor-not-allowed shadow-emerald-600/10"
                            : "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20 hover:-translate-y-1"
                        }`}
                      >
                        <i
                          className={`fa-solid ${
                            isSavingToDashboard || hasSavedToDashboard ? "fa-check" : "fa-cube"
                          }`}
                        ></i>
                        {isSavingToDashboard
                          ? "Saving..."
                          : hasSavedToDashboard
                          ? "Saved to Dashboard"
                          : "Save & Mint"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* START EXAM MODAL */}
          {isStartModalOpen && activeSubject && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
                onClick={() => setIsStartModalOpen(false)}
              ></div>
              <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden relative z-10 animate-in zoom-in-95 duration-200">
                <div
                  className={`h-32 relative overflow-hidden flex items-center justify-center bg-gradient-to-br ${
                    styles[activeSubject.color || "blue"].gradient
                  } to-slate-600`}
                >
                  <div className="absolute inset-0 bg-black/10"></div>
                  <button
                    onClick={() => setIsStartModalOpen(false)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/20 text-white hover:bg-white hover:text-red-500 flex items-center justify-center transition"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>
                <div className="px-8 pb-8 -mt-12 relative">
                  <div
                    className={`w-24 h-24 rounded-2xl bg-white shadow-xl flex items-center justify-center text-4xl mb-4 mx-auto ${
                      styles[activeSubject.color || "blue"].iconText
                    }`}
                  >
                    <i className="fa-solid fa-book"></i>
                  </div>
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-black text-slate-800 mb-1">{activeSubject.name}</h2>
                    <p className="text-slate-500 text-sm">Ready to challenge yourself?</p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block text-center mb-2">
                      Select Time Limit
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {[0, 60, 120, 180].map((val) => (
                        <button
                          key={val}
                          onClick={() => setSessionTime(val)}
                          className={`py-3 rounded-xl border font-bold text-xs transition-all ${
                            sessionTime === val
                              ? "border-indigo-600 bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
                              : "border-slate-200 text-slate-500 hover:border-indigo-300"
                          }`}
                        >
                          {val === 0
                            ? "No Limit"
                            : val === 60
                            ? "1 Hour"
                            : val === 120
                            ? "2 Hours"
                            : "3 Hours"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-8">
                    <button
                      onClick={launchExam}
                      className={`w-full py-4 rounded-xl font-bold text-white shadow-xl transition-all hover:scale-[1.02] ${
                        styles[activeSubject.color || "blue"].button
                      }`}
                    >
                      Begin Exam
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StudentMadeExam;
